import type maplibregl from 'maplibre-gl';
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import { MAP_COLORS, MAP_GEOJSON_URLS } from '@/core/constants/dashboard-map.constants';
import {
  buildDistrictPlaceLabels,
  buildNationalBoundaryFromProvinces,
  buildProvincePlaceLabels,
  normalizeDistrictGeoJson,
  normalizeProvinceGeoJson,
} from '@/core/services/mapGeometry.service';

const VIETNAM_BOUNDARY_SOURCE = 'vietnam-boundary';
const VIETNAM_PROVINCES_SOURCE = 'vietnam-provinces';
const VIETNAM_DISTRICTS_SOURCE = 'vietnam-districts';
const VIETNAM_PLACES_SOURCE = 'vietnam-places';
const VIETNAM_DISTRICT_LABELS_SOURCE = 'vietnam-district-labels';
const WAREHOUSE_AREA_SOURCE = 'warehouse-area-highlight';

let provincesLoaded: FeatureCollection | null = null;
let districtsLoaded: FeatureCollection | null = null;
let boundaryLoaded: FeatureCollection | null = null;

async function fetchGeoJson(url: string): Promise<FeatureCollection> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load GeoJSON: ${url}`);
  }
  return response.json();
}

/**
 * Prefer province-derived national outline so the highlighted border matches
 * administrative province edges. Coarse vietnam-boundary.geojson is fallback only.
 * No outside-region dimming — neighboring geography stays visible.
 */
export async function ensureMapBoundarySources(
  map: maplibregl.Map
): Promise<void> {
  if (!map.getSource(VIETNAM_PROVINCES_SOURCE)) {
    const provinces = normalizeProvinceGeoJson(
      await fetchGeoJson(MAP_GEOJSON_URLS.vietnamProvinces)
    );
    provincesLoaded = provinces;
    map.addSource(VIETNAM_PROVINCES_SOURCE, {
      type: 'geojson',
      data: provinces,
    });
  }

  if (!map.getSource(VIETNAM_BOUNDARY_SOURCE)) {
    const derived = provincesLoaded
      ? buildNationalBoundaryFromProvinces(provincesLoaded)
      : { type: 'FeatureCollection' as const, features: [] };

    const boundary =
      derived.features.length > 0
        ? derived
        : await fetchGeoJson(MAP_GEOJSON_URLS.vietnamBoundary);

    boundaryLoaded = boundary;
    map.addSource(VIETNAM_BOUNDARY_SOURCE, {
      type: 'geojson',
      data: boundary,
    });
  }

  if (!map.getSource(VIETNAM_DISTRICTS_SOURCE)) {
    try {
      const districts = normalizeDistrictGeoJson(
        await fetchGeoJson(MAP_GEOJSON_URLS.vietnamDistricts)
      );
      districtsLoaded = districts;
      map.addSource(VIETNAM_DISTRICTS_SOURCE, {
        type: 'geojson',
        data: districts,
      });
      map.addSource(VIETNAM_DISTRICT_LABELS_SOURCE, {
        type: 'geojson',
        data: buildDistrictPlaceLabels(districts),
      });
    } catch {
      districtsLoaded = { type: 'FeatureCollection', features: [] };
      map.addSource(VIETNAM_DISTRICTS_SOURCE, {
        type: 'geojson',
        data: districtsLoaded,
      });
      map.addSource(VIETNAM_DISTRICT_LABELS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
  }

  if (!map.getSource(VIETNAM_PLACES_SOURCE) && provincesLoaded) {
    map.addSource(VIETNAM_PLACES_SOURCE, {
      type: 'geojson',
      data: buildProvincePlaceLabels(provincesLoaded),
    });
  }

  if (!map.getSource(WAREHOUSE_AREA_SOURCE)) {
    map.addSource(WAREHOUSE_AREA_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  // Remove legacy outside-region dim sources/layers if present from hot reload.
  if (map.getLayer('neighbors-dim')) {
    map.removeLayer('neighbors-dim');
  }
  if (map.getLayer('outside-vietnam-mask')) {
    map.removeLayer('outside-vietnam-mask');
  }
  if (map.getSource('neighbors-dim')) {
    map.removeSource('neighbors-dim');
  }
}

/**
 * Soft solid Vietnam national border: glow + soft outer + crisp main.
 */
export function applyVietnamBoundaryStyle(map: maplibregl.Map): void {
  if (!map.getLayer('vietnam-border-glow')) {
    map.addLayer({
      id: 'vietnam-border-glow',
      type: 'line',
      source: VIETNAM_BOUNDARY_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAP_COLORS.vietnamBorderGlow,
        'line-width': 7,
        'line-opacity': 0.14,
        'line-blur': 5,
      },
    });
  }

  if (!map.getLayer('vietnam-border-soft')) {
    map.addLayer({
      id: 'vietnam-border-soft',
      type: 'line',
      source: VIETNAM_BOUNDARY_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAP_COLORS.vietnamBorderSoft,
        'line-width': 3.5,
        'line-opacity': 0.42,
        'line-blur': 1.5,
      },
    });
  }

  if (!map.getLayer('vietnam-border-main')) {
    map.addLayer({
      id: 'vietnam-border-main',
      type: 'line',
      source: VIETNAM_BOUNDARY_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAP_COLORS.vietnamBorder,
        'line-width': 1.7,
        'line-opacity': 0.95,
      },
    });
  }

  if (map.getLayer('vietnam-border')) {
    map.removeLayer('vietnam-border');
  }
}

export function applyVietnamPlaceLabels(map: maplibregl.Map): void {
  if (!map.getSource(VIETNAM_PLACES_SOURCE) || map.getLayer('vietnam-place-labels')) {
    return;
  }

  map.addLayer({
    id: 'vietnam-place-labels',
    type: 'symbol',
    source: VIETNAM_PLACES_SOURCE,
    minzoom: 4.5,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        10,
        8,
        11,
        10,
        13,
      ],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-optional': true,
      'symbol-sort-key': ['get', 'rank'],
    },
    paint: {
      'text-color': MAP_COLORS.placeLabel,
      'text-halo-color': 'rgba(255,255,255,0.96)',
      'text-halo-width': 2,
      'text-halo-blur': 1,
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        4.5,
        0.55,
        6,
        0.85,
        8,
        0.95,
      ],
    },
  });
}

/**
 * District boundaries + labels (visible for unit scope when province is known).
 * Layer order: after province border, before markers/callouts.
 */
export function applyDistrictLayers(map: maplibregl.Map): void {
  if (!map.getSource(VIETNAM_DISTRICTS_SOURCE)) {
    return;
  }

  if (!map.getLayer('district-boundaries')) {
    map.addLayer({
      id: 'district-boundaries',
      type: 'line',
      source: VIETNAM_DISTRICTS_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        visibility: 'none',
      },
      paint: {
        'line-color': MAP_COLORS.districtBorder,
        'line-width': 0.7,
        'line-opacity': 0.7,
      },
      filter: ['==', ['get', 'province_code'], ''],
    });
  }

  if (
    map.getSource(VIETNAM_DISTRICT_LABELS_SOURCE) &&
    !map.getLayer('district-labels')
  ) {
    map.addLayer({
      id: 'district-labels',
      type: 'symbol',
      source: VIETNAM_DISTRICT_LABELS_SOURCE,
      minzoom: 7.2,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'name'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          7.5,
          9,
          9,
          11,
          11,
          12,
        ],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-optional': true,
      },
      paint: {
        'text-color': MAP_COLORS.districtLabel,
        'text-halo-color': 'rgba(255,255,255,0.96)',
        'text-halo-width': 1.5,
        'text-halo-blur': 0.5,
      },
      filter: ['==', ['get', 'province_code'], ''],
    });
  }
}

export function setDistrictVisibility(
  map: maplibregl.Map,
  visible: boolean,
  provinceCode: string | null | undefined
): void {
  const filter = provinceCode
    ? (['==', ['get', 'province_code'], String(provinceCode)] as maplibregl.FilterSpecification)
    : (['==', ['get', 'province_code'], ''] as maplibregl.FilterSpecification);
  const visibility = visible && provinceCode ? 'visible' : 'none';

  if (map.getLayer('district-boundaries')) {
    map.setFilter('district-boundaries', filter);
    map.setLayoutProperty('district-boundaries', 'visibility', visibility);
  }
  if (map.getLayer('district-labels')) {
    map.setFilter('district-labels', filter);
    map.setLayoutProperty('district-labels', 'visibility', visibility);
  }
}

export function addVietnamBoundaryLayers(map: maplibregl.Map): void {
  // Subtle national fill — neighbors remain undimmed and readable.
  if (!map.getLayer('vietnam-fill')) {
    map.addLayer({
      id: 'vietnam-fill',
      type: 'fill',
      source: VIETNAM_BOUNDARY_SOURCE,
      paint: {
        'fill-color': MAP_COLORS.vietnamFill,
        'fill-opacity': 0.12,
      },
    });
  } else {
    map.setPaintProperty('vietnam-fill', 'fill-color', MAP_COLORS.vietnamFill);
    map.setPaintProperty('vietnam-fill', 'fill-opacity', 0.12);
  }

  if (!map.getLayer('selected-province-fill')) {
    map.addLayer({
      id: 'selected-province-fill',
      type: 'fill',
      source: VIETNAM_PROVINCES_SOURCE,
      paint: {
        'fill-color': MAP_COLORS.selectedProvinceFill,
        'fill-opacity': 0.12,
      },
      filter: ['==', ['get', 'province_code'], ''],
    });
  } else {
    map.setPaintProperty(
      'selected-province-fill',
      'fill-color',
      MAP_COLORS.selectedProvinceFill
    );
    map.setPaintProperty('selected-province-fill', 'fill-opacity', 0.12);
  }

  applyVietnamBoundaryStyle(map);

  if (!map.getLayer('province-boundaries')) {
    map.addLayer({
      id: 'province-boundaries',
      type: 'line',
      source: VIETNAM_PROVINCES_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAP_COLORS.provinceBorder,
        'line-width': 0.8,
        'line-opacity': 0.62,
      },
    });
  }

  if (!map.getLayer('selected-province-outline')) {
    map.addLayer({
      id: 'selected-province-outline',
      type: 'line',
      source: VIETNAM_PROVINCES_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAP_COLORS.vietnamBorder,
        'line-width': 2,
        'line-opacity': 0.9,
      },
      filter: ['==', ['get', 'province_code'], ''],
    });
  }

  applyDistrictLayers(map);
  applyVietnamPlaceLabels(map);

  if (!map.getLayer('warehouse-area-fill')) {
    map.addLayer({
      id: 'warehouse-area-fill',
      type: 'fill',
      source: WAREHOUSE_AREA_SOURCE,
      paint: {
        'fill-color': MAP_COLORS.hullBorder,
        'fill-opacity': 0.07,
      },
    });
  }

  if (!map.getLayer('warehouse-area-border')) {
    map.addLayer({
      id: 'warehouse-area-border',
      type: 'line',
      source: WAREHOUSE_AREA_SOURCE,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAP_COLORS.hullBorder,
        'line-width': 2,
        'line-opacity': 0.9,
        'line-dasharray': [3, 2],
      },
    });
  }
}

export function setSelectedProvinceFilter(
  map: maplibregl.Map,
  provinceCode: string | null | undefined
): void {
  const filter = provinceCode
    ? (['==', ['get', 'province_code'], String(provinceCode)] as maplibregl.FilterSpecification)
    : (['==', ['get', 'province_code'], ''] as maplibregl.FilterSpecification);

  if (map.getLayer('selected-province-fill')) {
    map.setFilter('selected-province-fill', filter);
  }
  if (map.getLayer('selected-province-outline')) {
    map.setFilter('selected-province-outline', filter);
  }
}

export function updateWarehouseAreaHighlight(
  map: maplibregl.Map,
  feature: Feature<Polygon> | null
): void {
  const source = map.getSource(WAREHOUSE_AREA_SOURCE) as
    | maplibregl.GeoJSONSource
    | undefined;
  if (!source) {
    return;
  }

  source.setData(
    feature
      ? { type: 'FeatureCollection', features: [feature] }
      : { type: 'FeatureCollection', features: [] }
  );
}

export function getProvincesGeoJson(): FeatureCollection | null {
  return provincesLoaded;
}

export function getDistrictsGeoJson(): FeatureCollection | null {
  return districtsLoaded;
}

export function getBoundaryGeoJson(): FeatureCollection | null {
  return boundaryLoaded;
}
